'use client'

import { Button, Text } from '@repo/ui'
import { LoaderGradient } from '@repo/ui/icons'
import { type ComponentProps, type ReactNode, useMemo } from 'react'

import { ChainMismatchButton } from '../../../components/ChainMismatchButton'
import { useAppKitSSR } from '../../../hooks/useAppKitSSR'
import { useUserAccount } from '../../../providers/UserAccountProvider'
import { isChainId } from '../../../utils'
import { useActionFlow } from '../ActionFlowProvider'

export function ActionFlowExecutionButton({
  className,
  disabled: disabledProp,
  ...props
}: ComponentProps<typeof Button>) {
  const { chainId, isLoading, state, execute, label } = useActionFlow()

  const { isConnected, isLoading: isAccountLoading, chain } = useUserAccount()
  const { open } = useAppKitSSR()
  const targetChainId = typeof chainId === 'number' && isChainId(chainId) ? chainId : undefined
  const showChainMismatchButton =
    isConnected &&
    !isAccountLoading &&
    state.flowState === 'idle' &&
    targetChainId !== undefined &&
    chain?.id !== targetChainId

  const {
    buttonContent,
    disabled,
    action
  }: { buttonContent: ReactNode; disabled: boolean; action?: () => void } = useMemo(() => {
    if (!isConnected) {
      return { buttonContent: 'Connect Wallet', disabled: false, action: () => open() }
    }

    switch (state.flowState) {
      case 'idle':
        return {
          buttonContent: label,
          disabled: isLoading || isAccountLoading || !!state.isRetryBlocked,
          action: execute
        }
      case 'execution':
        return state.flowState === 'execution' && state.stepState === 'pending-wallet'
          ? { buttonContent: 'Pending Wallet...', disabled: true }
          : {
              buttonContent: (
                <>
                  <LoaderGradient className="inline shrink-0" /> Pending transaction
                </>
              ),
              disabled: true
            }
      case 'success':
        return { buttonContent: 'Success', disabled: true }
      case 'reverted':
        return { buttonContent: 'Reverted', disabled: true }
    }
  }, [isConnected, state, label, isLoading, isAccountLoading, open, execute])

  const errorMessage = state.flowState === 'idle' && state.errorMessage && (
    <Text
      size="3xs"
      className="text-destructive-foreground max-h-12 overflow-y-auto break-all"
      data-testid="action-flow-error-message"
    >
      {state.errorMessage}
    </Text>
  )

  if (showChainMismatchButton) {
    return (
      <div className="flex w-full flex-col gap-1">
        <ChainMismatchButton
          desiredChainId={targetChainId}
          size="lg"
          className={className}
          disabled={disabledProp}
          {...props}
        />
        {errorMessage}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-1">
      <Button
        disabled={disabled || disabledProp}
        onClick={action}
        className={className}
        size="lg"
        {...props}
      >
        {buttonContent}
      </Button>
      {errorMessage}
    </div>
  )
}
